import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";
import { api, formatEUR, formatNum, formatApiError } from "@/lib/api";
import { ArrowRight, Warning, CheckCircle, Info } from "@phosphor-icons/react";

export default function PublicReport() {
    const { token } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        async function load() {
            try {
                const res = await api.get(`/public/report/${token}`);
                setData(res.data);
            } catch (e) {
                setError(formatApiError(e, "Informe no disponible"));
            } finally {
                setLoading(false);
            }
        }
        load();
    }, [token]);

    if (loading) return <div className="min-h-screen flex items-center justify-center"><span className="elegant-loader"><span /><span /><span /></span></div>;
    if (error || !data) {
        return (
            <div className="min-h-screen flex items-center justify-center px-6">
                <div className="text-center max-w-md">
                    <Warning size={48} weight="light" className="mx-auto mb-4 text-[var(--gf-warning)]" />
                    <h1 className="serif text-3xl mb-3">Informe no disponible</h1>
                    <p className="text-[var(--gf-text-secondary)] mb-6">{error || "El link ha sido revocado o no es válido."}</p>
                    <Link to="/" className="btn-gold">Ir a Gestión Fiscal 347</Link>
                </div>
            </div>
        );
    }

    const ratios = data.ratios?.ratios || {};
    const totales = data.ratios?.totales || {};
    const balance = data.balance || {};
    const interp = data.interpretation;

    const balanceChart = [
        { name: "Activos corrientes", value: balance.activos_corrientes },
        { name: "Activos no corrientes", value: balance.activos_no_corrientes },
        { name: "Pasivos corrientes", value: balance.pasivos_corrientes },
        { name: "Pasivos no corrientes", value: balance.pasivos_no_corrientes },
        { name: "Patrimonio neto", value: balance.patrimonio_neto },
    ];
    const pie = [
        { name: "Patrimonio neto", value: balance.patrimonio_neto || 0 },
        { name: "Pasivos totales", value: (balance.pasivos_corrientes || 0) + (balance.pasivos_no_corrientes || 0) },
    ];

    return (
        <div className="min-h-screen">
            {/* Top banner boutique */}
            <header className="bg-[var(--gf-navy)] text-white">
                <div className="max-w-5xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-3">
                        <img src="/logo.png" alt="GF347" className="h-10 w-auto" style={{ filter: "brightness(0) invert(1)" }} />
                    </Link>
                    <div className="text-xs text-white/60 uppercase tracking-widest">Informe compartido · Solo lectura</div>
                </div>
            </header>

            <main className="max-w-5xl mx-auto px-6 md:px-12 py-12">
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                    <div className="mb-8 pb-6 border-b border-[var(--gf-border)]">
                        <div className="micro-label mb-3">— Informe financiero</div>
                        <h1 className="serif text-4xl md:text-5xl">{data.company_name}</h1>
                        <div className="mt-2 flex gap-4 text-sm text-[var(--gf-text-secondary)]">
                            <span>Ejercicio <span className="mono font-medium">{data.period}</span></span>
                            <span>·</span>
                            <span>{new Date(data.created_at).toLocaleDateString("es-ES", { year: "numeric", month: "long", day: "numeric" })}</span>
                        </div>
                    </div>

                    <section className="grid md:grid-cols-3 gap-6 mb-10">
                        <Total label="Activos totales" v={totales.activos_totales} />
                        <Total label="Pasivos totales" v={totales.pasivos_totales} />
                        <Total label="Patrimonio neto" v={totales.patrimonio_neto} gold />
                    </section>

                    <section className="mb-12">
                        <div className="micro-label mb-3">— Ratios financieros</div>
                        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-[var(--gf-border)] border border-[var(--gf-border)]">
                            {Object.entries(ratios).map(([key, r]) => (
                                <RatioCell key={key} r={r} isPct={["roe", "roa", "margen_neto", "endeudamiento", "autonomia_financiera"].includes(key)} />
                            ))}
                        </div>
                    </section>

                    <section className="grid lg:grid-cols-2 gap-6 mb-12">
                        <div className="gf-card">
                            <div className="micro-label mb-4">— Composición</div>
                            <ResponsiveContainer width="100%" height={280}>
                                <BarChart data={balanceChart} layout="vertical" margin={{ left: 20 }}>
                                    <CartesianGrid strokeDasharray="2 2" stroke="#E5E0D8" />
                                    <XAxis type="number" tickFormatter={(v) => formatEUR(v)} tick={{ fontSize: 10 }} stroke="#8B95A5" />
                                    <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={140} stroke="#8B95A5" />
                                    <Tooltip formatter={(v) => formatEUR(v)} />
                                    <Bar dataKey="value" fill="#0B192C" />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                        <div className="gf-card">
                            <div className="micro-label mb-4">— Estructura financiera</div>
                            <ResponsiveContainer width="100%" height={280}>
                                <PieChart>
                                    <Pie data={pie} dataKey="value" innerRadius={50} outerRadius={100}>
                                        <Cell fill="#C5A880" /><Cell fill="#0B192C" />
                                    </Pie>
                                    <Tooltip formatter={(v) => formatEUR(v)} />
                                    <Legend iconType="square" />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    </section>

                    {interp && (
                        <section className="mb-12">
                            <div className="bg-[var(--gf-navy)] text-white p-8 mb-6">
                                <div className="micro-label text-[var(--gf-gold)] mb-3">Resumen ejecutivo</div>
                                <p className="serif text-2xl leading-snug">{interp.resumen_ejecutivo}</p>
                            </div>
                            <div className="grid md:grid-cols-2 gap-6">
                                {interp.interpretacion?.map((b, i) => (
                                    <div key={i} className="gf-card-ficha">
                                        <div className="micro-label mb-3">— {String(i + 1).padStart(2, "0")}</div>
                                        <h3 className="serif text-xl mb-3">{b.titulo}</h3>
                                        <p className="text-sm text-[var(--gf-text-secondary)] leading-relaxed">{b.contenido}</p>
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}

                    {/* Marketing CTA */}
                    <div className="bg-[var(--gf-navy)] text-white p-10 text-center mt-10">
                        <div className="micro-label text-[var(--gf-gold)] mb-3">— Powered by Gestión Fiscal 347</div>
                        <h3 className="serif text-3xl mb-3">¿Quieres un informe así para tu empresa?</h3>
                        <p className="text-white/70 mb-6 max-w-2xl mx-auto">
                            Análisis financiero profesional con IA en 2 minutos. Prueba gratis sin tarjeta.
                        </p>
                        <Link to="/registro" className="btn-gold inline-flex">
                            Comenzar análisis <ArrowRight size={16} />
                        </Link>
                    </div>
                </motion.div>
            </main>

            <footer className="bg-[var(--gf-navy)] text-white/60 text-center py-6 text-xs">
                © 2026 Gestión Fiscal 347 · <a href="/aviso-legal" className="hover:text-[var(--gf-gold)]">Aviso legal</a> · <a href="/privacidad" className="hover:text-[var(--gf-gold)]">Privacidad</a>
            </footer>
        </div>
    );
}

function Total({ label, v, gold }) {
    return (
        <div className={`gf-card-ficha ${gold ? "border-t-2 border-t-[var(--gf-gold)]" : ""}`}>
            <div className="micro-label mb-3">{label}</div>
            <div className="editorial-num mono">{formatEUR(v || 0)}</div>
        </div>
    );
}

function RatioCell({ r, isPct }) {
    const color = { bueno: "var(--gf-success)", alerta: "var(--gf-warning)", "n/a": "var(--gf-text-muted)" }[r.health] || "var(--gf-text-muted)";
    const Icon = { bueno: CheckCircle, alerta: Warning, "n/a": Info }[r.health] || Info;
    const display = r.value === null || r.value === undefined ? "—" : isPct ? `${formatNum(r.value * 100, 1)}%` : formatNum(r.value, 2);
    return (
        <div className="bg-white p-5">
            <div className="text-xs text-[var(--gf-text-secondary)] mb-2 h-10">{r.label}</div>
            <div className="editorial-num mono text-[2rem]">{display}</div>
            <div className="flex items-center gap-1 mt-3 text-xs" style={{ color }}>
                <Icon size={13} weight="fill" />
                <span className="uppercase tracking-wider font-medium">{r.health === "bueno" ? "Bueno" : r.health === "alerta" ? "Atención" : "N/A"}</span>
            </div>
        </div>
    );
}
